import type { FC } from 'react'
import { ToolUseChip } from './tool-use-chip.js'
import type { InFlightAgentStream } from './use-in-flight-agent-stream.js'
import styles from './live-agent-response.module.css'

export interface LiveAgentResponseProps {
  stream: InFlightAgentStream
  /** Optional accent color for the tool-use chips — passed through from
   *  the agent context so chips feel "owned" by the running agent. */
  accentColor?: string
}

/**
 * Synthetic feed item rendered while an agent stream is in flight. Sits at
 * the tail of the chat thread and shows:
 *  - the streamed text body (plain text — phase 2 defers markdown to a
 *    follow-up so the sidebar can ship now)
 *  - a blinking caret while `pending` (respects `prefers-reduced-motion`)
 *  - one {@link ToolUseChip} per tool call, in invocation order
 *
 * The outer wrapper carries `aria-live="polite"` so screen readers announce
 * streamed updates without interrupting the user. `data-request-id` exposes
 * the cache key so end-to-end tests (and the future flicker-free transition)
 * can match the synthetic item against the persisted comment that replaces it.
 *
 * Plain text is intentional. The popup uses react-markdown; sidebar markdown
 * is a phase-2 follow-up. Defaulting to text avoids partial-fence rendering
 * artefacts while the stream is mid-flight.
 */
export const LiveAgentResponse: FC<LiveAgentResponseProps> = props => {
  const { stream, accentColor } = props
  const { content, toolCalls, pending, error, requestId } = stream

  return (
    <div
      className={styles.container}
      aria-live="polite"
      data-request-id={requestId}
      data-testid="live-agent-response"
    >
      <div className={styles.body}>
        {content}
        {pending ? (
          <span className={styles.caret} data-testid="live-caret" aria-hidden="true">
            ▍
          </span>
        ) : null}
      </div>

      {toolCalls.length > 0 ? (
        <div className={styles.chips} role="group" aria-label="Tool calls">
          {toolCalls.map(toolCall => (
            <ToolUseChip key={toolCall.id} toolCall={toolCall} accentColor={accentColor} />
          ))}
        </div>
      ) : null}

      {error ? <div className={styles.error}>{error}</div> : null}
    </div>
  )
}
